import * as React from 'react'
import { Download } from 'lucide-react'
import {
  Alert,
  Drawer,
  IconButton,
  Spinner,
  Tooltip,
  useToast,
} from '@app/ui'
import { downloadBlob } from '@/helpers/downloadBlob'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'

export type PdfPreviewSource = {
  /** Header label (often the same as fileName). */
  title: string
  fileName: string
  load: () => Promise<Blob>
}

type PdfPreviewContextValue = {
  open: (source: PdfPreviewSource) => void
  download: (source: PdfPreviewSource) => Promise<void>
  close: () => void
}

/** Width the user last dragged the tray to, shared by every PDF preview. */
const PDF_PREVIEW_WIDTH_KEY = 'app.pdfPreviewWidth'

const PdfPreviewContext = React.createContext<PdfPreviewContextValue | null>(
  null,
)

/**
 * Right-hand PDF preview tray. Call {@link usePdfPreview} from any screen
 * that lists a PDF (view icon opens this drawer).
 */
export function PdfPreviewProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { toast } = useToast()
  const [source, setSource] = React.useState<PdfPreviewSource | null>(null)
  // The outcome of loading `source`, tagged with the source it belongs to so a
  // stale result is never shown for a different PDF. Loading is "no outcome yet
  // for the current source".
  const [result, setResult] = React.useState<{
    source: PdfPreviewSource
    objectUrl: string | null
    error: string | null
  } | null>(null)
  const current = source != null && result?.source === source ? result : null
  const objectUrl = current?.objectUrl ?? null
  const error = current?.error ?? null
  const loading = source != null && current == null

  const close = React.useCallback(() => {
    setSource(null)
    setResult(null)
  }, [])

  React.useEffect(() => {
    if (source == null) return
    let cancelled = false
    let createdUrl: string | null = null
    void source
      .load()
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        createdUrl = url
        setResult({ source, objectUrl: url, error: null })
      })
      .catch((err) => {
        if (cancelled) return
        const message = isApiError(err)
          ? (err.body.message ?? 'Could not load PDF')
          : toErrorMessage(err)
        setResult({ source, objectUrl: null, error: message })
      })
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [source])

  const download = React.useCallback(
    async (next: PdfPreviewSource): Promise<void> => {
      try {
        const blob = await next.load()
        downloadBlob(blob, next.fileName)
      } catch (err) {
        toast({
          title: isApiError(err)
            ? (err.body.message ?? 'Could not download PDF')
            : toErrorMessage(err),
          tone: 'danger',
        })
      }
    },
    [toast],
  )

  const open = React.useCallback((next: PdfPreviewSource) => {
    setSource(next)
  }, [])

  const value = React.useMemo(
    (): PdfPreviewContextValue => ({ open, download, close }),
    [open, download, close],
  )

  return (
    <PdfPreviewContext.Provider value={value}>
      {children}
      <Drawer
        open={source != null}
        onClose={close}
        side="right"
        size="xl"
        resizable
        widthStorageKey={PDF_PREVIEW_WIDTH_KEY}
        padded={false}
        title={source?.title ?? 'PDF'}
        headerRight={
          source ? (
            <Tooltip content="Download">
              <IconButton
                label="Download"
                disabled={loading}
                onClick={() => void download(source)}
              >
                <Download />
              </IconButton>
            </Tooltip>
          ) : null
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center gap-2 text-ds-sm text-ds-muted">
            <Spinner size="sm" label="Loading" />
            Loading PDF…
          </div>
        ) : null}
        {error ? (
          <div className="p-4">
            <Alert tone="danger" title="Could not load PDF">
              {error}
            </Alert>
          </div>
        ) : null}
        {objectUrl && !loading && error == null ? (
          <iframe
            aria-label={source?.fileName ?? 'PDF preview'}
            src={objectUrl}
            className="h-full w-full border-0 bg-ds-subtle"
          />
        ) : null}
      </Drawer>
    </PdfPreviewContext.Provider>
  )
}

export function usePdfPreview(): PdfPreviewContextValue {
  const ctx = React.useContext(PdfPreviewContext)
  if (ctx == null) {
    throw new Error('usePdfPreview must be used within PdfPreviewProvider')
  }
  return ctx
}
